import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Plus,
  Target,
  BarChart3,
  TrendingUp,
  DollarSign,
  Search,
  Download,
  FileText,
  Edit,
  Trash2,
  Eye
} from 'lucide-react'
import { 
  mockManualTestTrades, 
  mockManualTestStats,
  mockStrategies 
} from '../utils/mockData'
import Navigation from '../components/Navigation'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

export default function DemoManualTestPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('trades')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterDirection, setFilterDirection] = useState<'all' | 'long' | 'short'>('all')
  const [filterPnL, setFilterPnL] = useState<'all' | 'profit' | 'loss'>('all')

  const tabs = [
    { id: 'trades', name: '交易记录', icon: Target },
    { id: 'stats', name: '统计分析', icon: BarChart3 },
    { id: 'charts', name: '图表分析', icon: TrendingUp },
  ]

  const filteredTrades = mockManualTestTrades.filter(trade => {
    const matchesSearch = trade.reason_entry.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trade.reason_exit.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesDirection = filterDirection === 'all' || trade.direction === filterDirection
    const matchesPnL = filterPnL === 'all' || 
                      (filterPnL === 'profit' && trade.pnl_amount > 0) ||
                      (filterPnL === 'loss' && trade.pnl_amount < 0)
    return matchesSearch && matchesDirection && matchesPnL
  })

  const renderTradesTab = () => (
    <div className="space-y-6">
      {/* 操作栏 */}
      <div className="card">
        <div className="flex flex-col gap-4">
          {/* 操作按钮 - 小屏幕下垂直布局 */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <button className="btn-primary flex items-center justify-center" title="新建手动测试">
              <Plus className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">新建手动测试</span>
            </button>
            <button className="btn-secondary flex items-center justify-center" title="导出CSV">
              <Download className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">导出CSV</span>
            </button>
            <button className="btn-secondary flex items-center justify-center" title="导出PDF">
              <FileText className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">导出PDF</span>
            </button>
          </div>
          
          {/* 搜索和筛选 - 小屏幕下垂直布局 */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* 搜索框 - 始终显示 */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索交易记录..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            
            {/* 筛选器 - 小屏幕下隐藏，大屏幕显示 */}
            <div className="hidden lg:flex items-center space-x-4">
              <select
                value={filterDirection}
                onChange={(e) => setFilterDirection(e.target.value as any)}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="all">所有方向</option>
                <option value="long">做多</option>
                <option value="short">做空</option>
              </select>
              <select
                value={filterPnL}
                onChange={(e) => setFilterPnL(e.target.value as any)}
                className="px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="all">所有盈亏</option>
                <option value="profit">盈利</option>
                <option value="loss">亏损</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* 交易记录表格 */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
          <table className="w-full min-w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-2 sm:p-4 text-ocean-secondary font-medium text-xs sm:text-sm">策略</th>
                <th className="text-left p-2 sm:p-4 text-ocean-secondary font-medium text-xs sm:text-sm hidden sm:table-cell">时间</th>
                <th className="text-left p-2 sm:p-4 text-ocean-secondary font-medium text-xs sm:text-sm">方向</th>
                <th className="text-left p-2 sm:p-4 text-ocean-secondary font-medium text-xs sm:text-sm">入场价格</th>
                <th className="text-left p-2 sm:p-4 text-ocean-secondary font-medium text-xs sm:text-sm hidden sm:table-cell">出场价格</th>
                <th className="text-left p-2 sm:p-4 text-ocean-secondary font-medium text-xs sm:text-sm hidden lg:table-cell">数量</th>
                <th className="text-left p-2 sm:p-4 text-ocean-secondary font-medium text-xs sm:text-sm">盈亏</th>
                <th className="text-left p-2 sm:p-4 text-ocean-secondary font-medium text-xs sm:text-sm hidden lg:table-cell">R值</th>
                <th className="text-left p-2 sm:p-4 text-ocean-secondary font-medium text-xs sm:text-sm">操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrades.map((trade) => {
                const strategy = mockStrategies.find(s => s.id === trade.strategy_id)
                return (
                  <tr key={trade.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="p-2 sm:p-4">
                      <div className="text-ocean-primary font-medium text-xs sm:text-sm">{strategy?.name}</div>
                      <div className="text-ocean-secondary text-xs hidden sm:block">{strategy?.market_symbol}</div>
                    </td>
                    <td className="p-2 sm:p-4 hidden sm:table-cell">
                      <div className="text-ocean-primary text-xs sm:text-sm">
                        {new Date(trade.entry_time).toLocaleDateString()}
                      </div>
                      <div className="text-ocean-secondary text-xs">
                        {new Date(trade.entry_time).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="p-2 sm:p-4">
                      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${
                        trade.direction === 'long' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {trade.direction === 'long' ? '做多' : '做空'}
                      </span>
                    </td>
                    <td className="p-2 sm:p-4">
                      <div className="text-ocean-primary font-medium text-xs sm:text-sm">${trade.entry_price.toLocaleString()}</div>
                    </td>
                    <td className="p-2 sm:p-4 hidden sm:table-cell">
                      <div className="text-ocean-primary font-medium text-xs sm:text-sm">${trade.exit_price.toLocaleString()}</div>
                    </td>
                    <td className="p-2 sm:p-4 hidden lg:table-cell">
                      <div className="text-ocean-primary font-medium text-xs sm:text-sm">{trade.size}</div>
                    </td>
                    <td className={`p-2 sm:p-4 font-semibold text-xs sm:text-sm ${
                      trade.pnl_amount > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      ${trade.pnl_amount.toLocaleString()}
                    </td>
                    <td className={`p-2 sm:p-4 font-semibold text-xs sm:text-sm hidden lg:table-cell ${
                      trade.pnl_r > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {trade.pnl_r.toFixed(2)}
                    </td>
                    <td className="p-2 sm:p-4">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button className="text-violet-300 hover:text-violet-400 p-1" title="查看">
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button className="text-green-300 hover:text-green-400 p-1" title="编辑">
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button className="text-red-300 hover:text-red-400 p-1" title="删除">
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredTrades.length === 0 && (
        <div className="card">
          <div className="text-center py-12">
            <Target className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-ocean-primary mb-2">暂无交易记录</h3>
            <p className="text-ocean-secondary mb-6">
              {searchTerm || filterDirection !== 'all' || filterPnL !== 'all' 
                ? '没有找到匹配的交易记录，请调整筛选条件' 
                : '开始记录您的第一笔手动测试交易'}
            </p>
            {!searchTerm && filterDirection === 'all' && filterPnL === 'all' && (
              <button className="btn-primary flex items-center mx-auto">
                <Plus className="w-4 h-4 mr-2" />
                添加交易记录
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )

  const renderStatsTab = () => (
    <div className="space-y-6">
      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
              <Target className="w-6 h-6 text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">总交易数</p>
              <p className="text-2xl font-bold text-gradient">{mockManualTestStats.total_trades}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/30">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">胜率</p>
              <p className="text-2xl font-bold text-gradient">{(mockManualTestStats.win_rate * 100).toFixed(1)}%</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-purple-500/20 rounded-xl border border-purple-500/30">
              <DollarSign className="w-6 h-6 text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">总盈亏</p>
              <p className="text-2xl font-bold text-gradient">${mockManualTestStats.total_pnl.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-orange-500/20 rounded-xl border border-orange-500/30">
              <BarChart3 className="w-6 h-6 text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">平均R值</p>
              <p className="text-2xl font-bold text-gradient">{mockManualTestStats.avg_r_value.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 详细统计 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">交易方向统计</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                <span className="text-ocean-primary">做多交易</span>
              </div>
              <div className="text-right">
                <div className="text-ocean-primary font-semibold">{mockManualTestStats.long_trades}</div>
                <div className="text-ocean-secondary text-sm">{(mockManualTestStats.long_win_rate * 100).toFixed(1)}% 胜率</div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-400 rounded-full mr-3"></div>
                <span className="text-ocean-primary">做空交易</span>
              </div>
              <div className="text-right">
                <div className="text-ocean-primary font-semibold">{mockManualTestStats.short_trades}</div>
                <div className="text-ocean-secondary text-sm">{(mockManualTestStats.short_win_rate * 100).toFixed(1)}% 胜率</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">风险统计</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-ocean-primary">最大R值</span>
              <span className="text-green-400 font-semibold">{mockManualTestStats.max_r_value.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-ocean-primary">最小R值</span>
              <span className="text-red-400 font-semibold">{mockManualTestStats.min_r_value.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-ocean-primary">最大回撤</span>
              <span className="text-red-400 font-semibold">${Math.abs(mockManualTestStats.max_drawdown).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-ocean-primary">平均持仓时间</span>
              <span className="text-blue-400 font-semibold">{mockManualTestStats.avg_trade_duration.toFixed(1)} 小时</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderChartsTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-4">盈亏趋势</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={mockManualTestTrades.map((trade, index) => ({
            trade: index + 1,
            pnl: trade.pnl_amount,
            cumulative: mockManualTestTrades.slice(0, index + 1).reduce((sum, t) => sum + t.pnl_amount, 0)
          }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="trade" stroke="#9ca3af" />
            <YAxis stroke="#9ca3af" />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="pnl" stroke="#3b82f6" strokeWidth={2} name="单笔盈亏" />
            <Line type="monotone" dataKey="cumulative" stroke="#10b981" strokeWidth={2} name="累计盈亏" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">胜率趋势</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mockManualTestTrades.map((_, index) => {
              const tradesSoFar = mockManualTestTrades.slice(0, index + 1)
              const wins = tradesSoFar.filter(t => t.pnl_amount > 0).length
              return {
                trade: index + 1,
                winRate: (wins / tradesSoFar.length) * 100
              }
            })}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="trade" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Line type="monotone" dataKey="winRate" stroke="#f59e0b" strokeWidth={2} name="胜率 (%)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">R值分布</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[
              { range: '0-0.5R', count: mockManualTestTrades.filter(t => t.pnl_r >= 0 && t.pnl_r <= 0.5).length },
              { range: '0.5-1R', count: mockManualTestTrades.filter(t => t.pnl_r > 0.5 && t.pnl_r <= 1).length },
              { range: '1-2R', count: mockManualTestTrades.filter(t => t.pnl_r > 1 && t.pnl_r <= 2).length },
              { range: '2-3R', count: mockManualTestTrades.filter(t => t.pnl_r > 2 && t.pnl_r <= 3).length },
              { range: '>3R', count: mockManualTestTrades.filter(t => t.pnl_r > 3).length },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="range" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Bar dataKey="count" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'trades':
        return renderTradesTab()
      case 'stats':
        return renderStatsTab()
      case 'charts':
        return renderChartsTab()
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8">
        {/* 页面标题 */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate('/demo')}
              className="btn-secondary flex items-center justify-center"
              title="返回主页"
            >
              <ArrowLeft className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">返回主页</span>
            </button>
            <div>
              <h2 className="text-3xl font-bold text-gradient mb-2">手动测试管理</h2>
              <p className="text-white/70">管理和分析手动测试交易记录</p>
            </div>
          </div>
        </div>

        {/* 标签页 */}
        <div className="card">
          <div className="border-b border-white/20">
            <div className="flex overflow-x-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-ocean-primary border-b-2 border-violet-500 bg-white/5'
                        : 'text-ocean-secondary hover:text-ocean-primary hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    {tab.name}
                  </button>
                )
              })}
            </div>
          </div>
          
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  )
} 